import { Component, EventEmitter, Input, OnInit, Output, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { filter, tap, map } from 'rxjs/operators';
import { Airport, PnrInfo } from 'src/app/core/models/itinerary.model';
import { ExchangeSegment } from 'src/app/core/models/voluntary-change.model';
import { ItineraryService } from 'src/app/search-result/services/itinerary.service';
import { VoluntaryChangeService } from 'src/app/voluntary-change/services/voluntary-change.service';
import { Observable, Subscription } from 'rxjs';
import { PAGETYPE } from 'src/app/core/enums/enums';
import { SettingsService } from 'src/app/settings/services/settings.service';
import { InvoluntaryChangeService } from 'src/app/involuntary-change/services/involuntary-change.service';
import {
  ModelRespondMachineLearning,
  PenaltyFareBasisMachineLearning
} from 'src/app/core/models/cancellation.model';

@Component({
  selector: 'app-search-alternatives',
  templateUrl: './search-alternatives.component.html',
  styleUrls: ['./search-alternatives.component.scss']
})
export class SearchAlternativesComponent implements OnInit, OnDestroy {
  @Input() exchangeSegments: ExchangeSegment[];
  @Input() validatingCarrier: Airport;
  @Input() isInvoluntary: boolean;
  @Input() isException: boolean;
  @Input() applyRulesML = false;
  @Input() enableCarrier: boolean;

  @Output() searchAvailabilities = new EventEmitter<ExchangeSegment[]>();

  pnrInfo: PnrInfo = null;
  isSubmitted = false;
  errorDates = false;
  errorML = false;
  errorSelected = false;
  form: FormGroup;
  maxSegments = 20;
  minSegments = 0;
  selectedFlights: number[][] = [];
  segmentEditMode = false;
  addedSegments = {};
  machineLearningRules: string;
  originalSegments: number;
  originalSegmentRemoved: boolean;
  originalSegmentCounter: number = 0;
  isVoluntary: boolean;
  isVoluntaryManual: boolean;
  isAirline$: Observable<boolean>;
  maximunValidReservationDateInMS: number = 0;
  originalExchangeSegments: { data: ExchangeSegment[]; isUpdated: boolean } = {
    data: null,
    isUpdated: false
  };

  subscriptions = new Subscription();

  constructor(
    private fb: FormBuilder,
    private itineraryService: ItineraryService,
    private settingsService: SettingsService,
    private vcService: VoluntaryChangeService,
    private icService: InvoluntaryChangeService,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    this.vcService.dispatchClearRemoveSegments();
    this.isVoluntary = window.location.href.includes('/' + PAGETYPE.voluntaryChange);
    this.isVoluntaryManual = window.location.href.includes('/' + PAGETYPE.newFlow);
    this.isAirline$ = this.settingsService.getClientIsAirline();
    this.originalSegments = this.exchangeSegments.length;
    this.exchangeSegments.map((x) => (x.enabled = true));
    this.buildForm();
    this.callComponentSubcriptions();
  }

  callComponentSubcriptions(): void {
    this.subscriptions.add(
      this.itineraryService.getPnrInfo().subscribe((info) => {
        this.pnrInfo = info;
      })
    );

    this.subscriptions.add(
      this.route.params.subscribe((params) => {
        if (params.hasOwnProperty('isException')) {
          this.vcService.getIsNewFlow().subscribe((res) => {
            if (res) {
              this.vcService
                .getVoluntaryExchangeQuote()
                .subscribe((res) => {
                  this.exchangeSegments = res.exchangeSegments;

                  this.setOverrideSegments(res.exchangeSegments);
                  this.searchAvailabilities.emit(this.exchangeSegments);
                })
                .unsubscribe();
            }
          });
        }
      })
    );

    this.subscriptions.add(
      this.icService.getReloadAvailabilities().subscribe((reload) => {
        reload ? this.setEmitSegments() : null;
      })
    );
  }

  setOverrideSegments(exchangeSegments) {
    let resIndex = [];
    exchangeSegments.map((resp) => {
      if (resp.enabled && typeof resp.originalIndex === 'number') {
        resIndex.push(resp.originalIndex);
      }
    });
    resIndex = resIndex.map((x) => x - 1);
    this.itineraryService.setOverrideSegments(resIndex);
  }

  setEmitSegments() {
    this.searchAvailabilities.emit(this.exchangeSegments);
  }

  buildForm() {
    const value = this.validatingCarrier
      ? `${this.validatingCarrier.name}  (${this.validatingCarrier.code})`
      : '';
    this.form = this.fb.group({
      validatingCarrier: [{ value, disabled: true }]
    });
  }

  validateDates(): void {
    this.subscriptions.add(
      this.itineraryService
        .getRulesAI()
        .pipe(
          filter(
            (rules): rules is PenaltyFareBasisMachineLearning =>
              rules && 'farebasisList' in rules && this.applyRulesML
          ),
          map((rules) =>
            rules?.farebasisList
              ?.map((item) => item.modelRespond)
              ?.flat()
              .filter((model) => model.boolean)
          ),
          tap(this.farebasisValidations.bind(this))
        )
        .subscribe()
    );
  }

  validateSelecteds(): boolean {
    return this.exchangeSegments?.some((seg) => seg.enabled);
  }

  validate(): boolean {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    if (this.isInvoluntary && !this.validateSelecteds()) {
      return false;
    }

    this.validateDates();

    if (this.yearlyTicketRestrictionValidation()) {
      this.errorDates = true;
      return false;
    } else {
      this.errorDates = false;
    }

    if (this.errorDates || this.errorML) {
      return false;
    }

    for (const flight of this.exchangeSegments) {
      if (new Date(flight.departureDateTime) < today) {
        return false;
      }
    }
    return true;
  }

  searchAvailability() {
    this.isSubmitted = true;

    if (!this.validate()) {
      return;
    }

    this.setOverrideSegments(this.exchangeSegments);
    this.setEmitSegments();
  }

  addSegment(originalIndex) {
    if (this.exchangeSegments.length >= this.maxSegments) {
      return;
    }
    let newIndex = 1;
    if (this.addedSegments[originalIndex]) {
      newIndex = ++this.addedSegments[originalIndex];
    } else {
      this.addedSegments[originalIndex] = newIndex;
    }
    const originalSegment = this.exchangeSegments.find(
      (seg) => seg.originalIndex === originalIndex
    );
    const newSegment = JSON.parse(JSON.stringify(originalSegment));
    newSegment.originalIndex = `${originalIndex}.${newIndex}`;
    const positionIndex = this.exchangeSegments.findIndex(
      (seg) => seg.originalIndex === originalIndex + 1
    );
    if (positionIndex >= 0) {
      this.exchangeSegments.splice(positionIndex, 0, newSegment);
    } else {
      this.exchangeSegments.push(newSegment);
    }
  }

  deleteSegment(index: number, originalIndex: any) {
    if (this.isOriginalSegment(originalIndex)) {
      this.originalSegmentCounter++;
      this.originalSegmentRemoved = true;

      if (!this.originalExchangeSegments.isUpdated) {
        this.originalExchangeSegments.data = JSON.parse(JSON.stringify(this.exchangeSegments));
      }
    }
    this.exchangeSegments.splice(index, 1);
    this.isOriginalSegment(originalIndex) && this.vcService.dispatchSetRemoveSegments(index);
  }

  restoreAllOriginalSegments() {
    this.originalExchangeSegments.isUpdated = false;
    this.originalSegmentCounter = 0;
    this.exchangeSegments = [];
    this.exchangeSegments = this.originalExchangeSegments.data;
    this.originalSegmentRemoved = false;
    this.vcService.dispatchRestoreAllRemovedSegments(this.originalExchangeSegments.data);
  }

  isOriginalSegment(originalIndex) {
    return !originalIndex.indexOf;
  }

  getCalendarId(flight) {
    return flight.originalIndex.toString().replace('.', '-');
  }

  private farebasisValidations(modelResponse: ModelRespondMachineLearning[]): void {
    if (this.stopversValidation(modelResponse)) {
      return;
    }

    if (this.modelCategory11Validation(modelResponse)) {
      return;
    }

    if (this.modelCategory2Validation(modelResponse)) {
      return;
    }

    if (this.modelCategory3Validation(modelResponse)) {
      return;
    }

    if (this.minMaxCategoryValidation(modelResponse)) {
      return;
    }

    if (this.validateDepartureArrivalDates()) {
      return;
    }

    this.machineLearningRules = '';
    this.errorML = false;
    this.errorDates = false;
  }

  private stopversValidation(modelResponse: ModelRespondMachineLearning[]): boolean {
    let response: boolean = false;

    const stopversValidation = this.validateStopOvers(
      modelResponse?.filter((model) => model.category === 8)[0]
    );

    if (stopversValidation) {
      this.errorML = true;
      this.machineLearningRules = stopversValidation;
      response = true;
    }

    return response;
  }

  private validateStopOvers(value: ModelRespondMachineLearning): string | null {
    return this.exchangeSegments?.length - this.originalSegments > value?.value?.[0]
      ? value.answer?.[0]
      : null;
  }

  private modelCategory11Validation(modelResponse: ModelRespondMachineLearning[]): boolean {
    let response: boolean = false;

    if (
      this.exchangeSegments.some((segment) =>
        modelResponse
          ?.filter((model) => model.category === 11)[0]
          ?.value?.map((date) => this.getDate(date))
          .includes(new Date(segment.departureDateTime).toISOString())
      )
    ) {
      this.machineLearningRules = modelResponse?.filter(
        (model) => model.category === 11
      )[0]?.answer?.[0];
      this.errorML = true;
      response = true;
    }

    return response;
  }

  private modelCategory2Validation(modelResponse: ModelRespondMachineLearning[]): boolean {
    let response: boolean = false;

    if (
      modelResponse?.filter((model) => model.category === 2)[0]?.value?.length &&
      !this.exchangeSegments.every((segment) =>
        modelResponse
          ?.filter((model) => model.category === 2)[0]
          ?.value.includes(
            new Date(segment.departureDateTime).toDateString().split(' ')[0].toUpperCase()
          )
      )
    ) {
      this.machineLearningRules = modelResponse?.filter(
        (model) => model.category === 2
      )[0]?.answer?.[0];
      this.errorML = true;
      response = true;
    }

    return response;
  }

  private modelCategory3Validation(modelResponse: ModelRespondMachineLearning[]): boolean {
    let response: boolean = false;

    if (modelResponse?.filter((model) => model.category === 3)[0]?.value?.length) {
      let hasError = false;
      const ranges = this.getDateRanges(
        modelResponse?.filter((model) => model.category === 3)[0]?.value
      );
      this.exchangeSegments.forEach((segment) => {
        const departure = new Date(segment.departureDateTime);
        hasError = !ranges.some((range) => {
          return departure >= range[0] && departure <= range[1];
        });
      });

      if (hasError) {
        this.machineLearningRules = modelResponse?.filter(
          (model) => model.category === 3
        )[0]?.answer?.[0];
        this.errorML = true;
        response = true;
      }
    }

    return response;
  }

  private minMaxCategoryValidation(modelResponse: ModelRespondMachineLearning[]): boolean {
    let response: boolean = false;

    const min = this.getStay(modelResponse?.filter((model) => model?.category === 6)[0]) ?? 0;
    const max = this.getStay(modelResponse?.filter((model) => model?.category === 7)[0]) ?? 0;

    if (min || max) {
      for (let index = 1; index < this.exchangeSegments.length; index++) {
        const departure = new Date(this.exchangeSegments[index - 1].departureDateTime);
        const arrival = new Date(this.exchangeSegments[index].departureDateTime);
        const diffDays = Math.ceil((arrival.getTime() - departure.getTime()) / 86400000);

        if (diffDays < min || diffDays > max) {
          this.machineLearningRules = `${
            modelResponse?.filter((model) => model.category === 6)[0]?.answer?.[0]
          } ${modelResponse?.filter((model) => model.category === 7)[0]?.answer?.[0]}`;
          this.errorML = true;
          response = true;
        }
      }
    }

    return response;
  }

  private validateDepartureArrivalDates(): boolean {
    let response: boolean = false;

    if (this.exchangeSegments.length === 2) {
      const departure = new Date(this.exchangeSegments[0]?.departureDateTime);
      const arrival = new Date(this.exchangeSegments[1]?.departureDateTime);
      if (arrival < departure) {
        this.errorDates = true;
        response = true;
      }
    }

    return response;
  }

  private yearlyTicketRestrictionValidation(): boolean {
    let response = false;
    let maximunSelectedDateInMS: number = 0;

    this.exchangeSegments?.forEach((segment) => {
      if (segment?.departureDateTime instanceof Date) {
        const currentMaximunSelectedDateInMS = segment?.departureDateTime.getTime();

        if (maximunSelectedDateInMS < currentMaximunSelectedDateInMS) {
          maximunSelectedDateInMS = currentMaximunSelectedDateInMS;
        }
      }
    });

    if (this.pnrInfo) {
      this.pnrInfo.passengers?.forEach((passenger) => {
        if (passenger) {
          const date = new Date(passenger.ticket.issuedDate);
          const currentYear = date.getUTCFullYear();
          date.setUTCFullYear(currentYear + 1);
          const currentMaximunValidReservationDateInMS = date.getTime();

          if (this.maximunValidReservationDateInMS < currentMaximunValidReservationDateInMS) {
            this.maximunValidReservationDateInMS = currentMaximunValidReservationDateInMS;
          }
        }
      });
    }

    maximunSelectedDateInMS > this.maximunValidReservationDateInMS
      ? (response = true)
      : (response = false);

    return response;
  }

  private getStay(modelRespond: ModelRespondMachineLearning): number {
    const denominations = {
      MONTH: () => modelRespond.value[0] * 30,
      DAY: () => modelRespond.value[0],
      YEAR: () => modelRespond.value[0] * 365,
      HOUR: () => modelRespond.value[0] / 24
    };

    return modelRespond?.denomination?.length ? denominations[modelRespond?.denomination[0]]() : 0;
  }

  private getDate(date: string): string {
    const dates = date.split('/');
    return new Date(Number(dates[2]), Number(dates[1]) - 1, Number(dates[0])).toISOString();
  }

  private getDateRanges(dates: string[]): Date[][] {
    let tempDates: Date[] = [];
    const finalDates: Date[][] = [];
    let counter = 0;

    const newDates = dates.map((date) => {
      const splitDate = date.split('/');
      return new Date(Number(splitDate[2]), Number(splitDate[1]) - 1, Number(splitDate[0]));
    });

    newDates.forEach((date) => {
      if (counter < 2) {
        tempDates.push(date);
        counter++;
      }

      if (counter === 2) {
        finalDates.push(tempDates);
        counter = 0;
        tempDates = [];
      }
    });
    return finalDates;
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
